import { FORM_ERROR } from 'final-form';
import React from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { patchInboundMerchant } from 'apis/merchant-onboarding-service/api';

import { Typography as T } from '../../../../common/typography';
import { Body3 } from '../../../../common/typography/Typography';
import { CountryConfig } from '../../../country/types';
import { MenuPhotoShootOption, SsmoCountryConfig } from '../../SelfServeFormView/SelfServeSignUpForm/types';
import MenuPhotoShootOptionForm from '../forms/ServicePricingForm/MenuPhotoShootOptionForm';
import { MenuPhotoShootOptionPreferences, MenuPhotoShootOptionPreferencesSchema } from '../forms/validation';
import { validateStepParam } from '../helpers';
import { InboundMerchant } from '../types';

type MenuPhotoshopOptionProps = {
  inboundMerchant: InboundMerchant;
  ssmoCountryConfig: SsmoCountryConfig;
  goToNextStep: () => void;
  setUpdateIsInProgress: (inProgress: boolean) => void;
  setNextButtonDisabled: (disabled: boolean) => void;
  countryConfig: CountryConfig;
};

const Wrapper = styled.div`
  margin-block-start: 32px;
  margin-block-end: 56px;
`;

const Title = styled(T.Title1)`
  display: block;
  width: 100%;
  font-weight: 700;
`;

const MenuPhotoshopOptionStep = ({
  inboundMerchant,
  ssmoCountryConfig,
  goToNextStep,
  setUpdateIsInProgress,
  setNextButtonDisabled,
  countryConfig,
}: MenuPhotoshopOptionProps) => {
  const intl = useIntl();
  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);

  const handleMenuPhotoShootOptionSubmit = async (
    values: MenuPhotoShootOptionPreferences,
  ): Promise<{ [key: string]: string | Error } | void> => {
    try {
      setUpdateIsInProgress(true);
      const validFormValues = MenuPhotoShootOptionPreferencesSchema.validateSync(values);
      const stepParam = validateStepParam(searchParams.get('step'));
      await patchInboundMerchant(
        {
          menuPhotoShootChoice: validFormValues.menuPhotoShootChoice as MenuPhotoShootOption,
          ssmoStepCompletionId: stepParam,
        },
        inboundMerchant.merchantId,
      );
      goToNextStep();
    } catch (submitError) {
      return { [FORM_ERROR]: submitError as Error };
    } finally {
      setUpdateIsInProgress(false);
    }
  };

  return (
    <Wrapper>
      <Title
        style={{
          marginBlockEnd: '8px',
        }}
      >
        {intl.formatMessage({ id: 'self-serve-sign-up.wizard.menu-photoshoot-options.title' })}
      </Title>
      <Body3>{intl.formatMessage({ id: 'self-serve-sign-up.wizard.menu-photoshoot-options.description' })}</Body3>
      <Title
        style={{
          marginBlockStart: '24px',
          marginBlockEnd: '16px',
        }}
      >
        {intl.formatMessage({ id: 'self-serve-sign-up.wizard.menu-photoshoot-options.select-preferences' })}
      </Title>
      <MenuPhotoShootOptionForm
        menuPhotoShootPricingConfig={ssmoCountryConfig.menuPhotoShootPricingConfig.prices}
        photoShootOptionChoice={inboundMerchant.menuPhotoShootChoice || undefined}
        handleSubmit={handleMenuPhotoShootOptionSubmit}
        setNextButtonDisabled={setNextButtonDisabled}
        countryConfig={countryConfig}
      />
    </Wrapper>
  );
};

export default MenuPhotoshopOptionStep;
